import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import type {
  ArInvoiceListRow,
  ArInvoiceStatus,
  CreateArInvoice,
  CreateArInvoiceLine,
} from '@dcc-erp/shared';
import {
  CreateArInvoiceSchema,
  dateToInputValue,
  fmt,
  fmtB,
  parseTermsDays,
  toNumber,
} from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { type ArTab, isDueSoon } from '../components/ar/ar-tabs';
import { ArInvoiceTable } from '../components/ar/ArInvoiceTable';
import { BulkBar } from '../components/ar/BulkBar';
import { CollectionForecast } from '../components/ar/CollectionForecast';
import { CreditLimitBar } from '../components/ar/CreditLimitBar';
import { type Density, getDensity, setDensity } from '../components/ar/density';
import { computeDso } from '../components/ar/dso';
import { buildFxFields, isFxIncomplete } from '../components/ar/fx';
import {
  modalBody,
  modalBox,
  modalError,
  modalFooter,
  modalHeader,
  modalLabel,
  modalOverlay,
  modalTitle,
} from '../components/ar/modal-style';
import { RecordReceiptModal } from '../components/ar/RecordReceiptModal';
import { type ArView, loadViews, saveView } from '../components/ar/saved-views';
import { matchesTerms } from '../components/ar/terms-filter';
import { Can } from '../components/Can';
import { BranchSelect, DepartmentSelect, DimensionFilter } from '../components/dimensions';
import { HelpButton } from '../components/help/HelpButton';
import { PageHead } from '../components/layout';
import {
  SpreadsheetImportModal,
  type ImportFieldSpec,
  type ParsedUnit,
  type ImportResult,
} from '../components/SpreadsheetImportModal';
import { usePermissions } from '../lib/auth/usePermissions';
import {
  useActiveCompany,
  useActiveCompanyId,
  useArCreditStatus,
  useArInvoices,
  useBranches,
  useCashflowForecast,
  useCoa,
  useCreateArInvoice,
  useCustomers,
  useDepartments,
} from '../lib/queries';
import { exportRows, headerIndexer, todayStamp, type ExportColumn } from '../lib/xlsx';

// =============================================================================
// AR live wiring
// =============================================================================

const SEARCH_BG =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%238a8a93' stroke-width='2'%3E%3Ccircle cx='11' cy='11' r='7'/%3E%3Cpath d='M21 21l-4.3-4.3'/%3E%3C/svg%3E\")";

const AR_TABS: readonly (readonly [ArTab, string])[] = [
  ['all', 'ทั้งหมด'],
  ['open', 'รอชำระ'],
  ['overdue', 'เกินกำหนด'],
  ['duesoon', 'ครบกำหนดเร็วๆนี้'],
  ['paid', 'ชำระแล้ว'],
];

// ---- Spreadsheet (นำเข้า / ส่งออก) constants -------------------------------
// One spreadsheet row = one AR invoice carrying a SINGLE revenue line. The
// header keys here are also the export header keys so an exported file
// round-trips straight back through the importer.

const AR_IMPORT_FIELDS: ImportFieldSpec[] = [
  { col: 'invoiceDate', label: 'วันที่ออก', required: true, hint: 'รูปแบบ ISO เช่น 2026-05-29' },
  { col: 'dueDate', label: 'กำหนดชำระ', required: true, hint: 'รูปแบบ ISO เช่น 2026-06-28' },
  {
    col: 'customerCode',
    label: 'รหัสลูกค้า',
    required: true,
    hint: 'ต้องมีอยู่จริงในทะเบียนลูกค้า',
  },
  {
    col: 'branchCode',
    label: 'รหัสสาขา',
    required: false,
    hint: 'เว้นว่างได้ — ถ้าระบุต้องมีอยู่จริง',
  },
  {
    col: 'departmentCode',
    label: 'รหัสหน่วยงาน',
    required: false,
    hint: 'เว้นว่างได้ — ถ้าระบุต้องมีอยู่จริง',
  },
  { col: 'description', label: 'คำอธิบาย', required: false, hint: 'รายละเอียดใบแจ้งหนี้ / รายการ' },
  {
    col: 'revenueCode',
    label: 'บัญชีรายได้',
    required: false,
    hint: 'รหัสบัญชีรายได้ (ค่าเริ่มต้น 40110001)',
  },
  {
    col: 'amount',
    label: 'ยอดเงิน (ก่อน VAT)',
    required: true,
    hint: 'มากกว่า 0 — ยอดของรายการเดียว',
  },
  { col: 'vatRate', label: 'อัตรา VAT (%)', required: false, hint: '0–100 (ค่าเริ่มต้น 7)' },
  {
    col: 'currencyCode',
    label: 'สกุลเงิน',
    required: false,
    hint: 'เว้นว่างได้ (ค่าเริ่มต้น THB)',
  },
];

// Header row written into the downloadable template (must match export keys).
const AR_TEMPLATE_COLUMNS = [
  'invoiceDate',
  'dueDate',
  'customerCode',
  'branchCode',
  'departmentCode',
  'description',
  'revenueCode',
  'amount',
  'vatRate',
  'currencyCode',
];

const AR_TEMPLATE_EXAMPLES: (string | number | boolean)[][] = [
  [
    '2026-05-29',
    '2026-06-28',
    'C-0001',
    '',
    '',
    'ขายกระเบื้องเซรามิค',
    '40110001',
    100000,
    7,
    'THB',
  ],
];

// Tab → server filter mapping. POSTED + overdue=true is needed because the
// "overdue" subset is a refinement of POSTED rows by dueDate, not a separate
// status enum — we ask the server to apply it so the count + list are exact.
function filtersForTab(tab: ArTab): { status?: ArInvoiceStatus; overdue?: boolean } {
  switch (tab) {
    case 'open':
      return { status: 'POSTED' };
    case 'paid':
      return { status: 'PAID' };
    case 'overdue':
      return { overdue: true };
    // duesoon is a client refinement of the POSTED set (server status filtering
    // unchanged); the isDueSoon predicate narrows POSTED rows to those due
    // within 7 days and not yet overdue.
    case 'duesoon':
      return { status: 'POSTED' };
    case 'all':
    default:
      return {};
  }
}

// Add `n` calendar days to a date without mutating the input (no date library).
function addDays(base: Date, n: number): Date {
  const d = new Date(base.getTime());
  d.setDate(d.getDate() + n);
  return d;
}

// Optional deep-link search param. The /ar/aging customer-drill navigates here
// with `customerId` to scope the list to one customer (mirrors ledger.tsx's
// validateSearch idiom).
export interface ArSearch {
  customerId?: string | undefined;
}

export const Route = createFileRoute('/ar')({
  validateSearch: (search: Record<string, unknown>): ArSearch => ({
    customerId: typeof search.customerId === 'string' ? search.customerId : undefined,
  }),
  component: ArPage,
});

export function ArPage() {
  const navigate = useNavigate();
  const companyId = useActiveCompanyId();
  const company = useActiveCompany();
  const { customerId: drillCustomerId } = Route.useSearch();
  const [tab, setTab] = useState<ArTab>('all');
  const [q, setQ] = useState<string>('');
  // Display density (ART-24). Initialised from the persisted choice so the
  // toggle reflects the class already applied to <html> on app boot.
  const [density, setDensityState] = useState<Density>(() => getDensity());
  function toggleDensity() {
    const next: Density = density === 'comfortable' ? 'compact' : 'comfortable';
    setDensity(next);
    setDensityState(next);
  }
  const [filterBranchId, setFilterBranchId] = useState<string>('');
  const [filterDepartmentId, setFilterDepartmentId] = useState<string>('');
  // เงื่อนไขชำระ (payment terms) is filtered client-side; the date range is a
  // server-side filter the API already supports via dateFrom/dateTo.
  const [termsFilter, setTermsFilter] = useState<string>('');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  // Saved filter views (ART-06), persisted in localStorage. Held in state so the
  // dropdown refreshes immediately after saving a new view.
  const [savedViews, setSavedViews] = useState<ArView[]>(() => loadViews());
  const [createOpen, setCreateOpen] = useState(false);
  const [showImport, setShowImport] = useState(false);
  // Collection-forecast panel (ART-18) — collapsed by default; its data is
  // lazily fetched only while expanded.
  const [forecastOpen, setForecastOpen] = useState(false);
  // Receipt modal preset. `{}` = opened from the primary "รับชำระ" action with
  // no preset (pick customer in-modal); a populated preset opens straight to the
  // row's customer + invoice. null = closed.
  const [receiptPreset, setReceiptPreset] = useState<{
    customerId?: string;
    invoiceId?: string;
    invoiceIds?: string[];
  } | null>(null);
  // Bulk-select state — wiring fleshed out in Task 9; minimal toggles here so
  // the ArInvoiceTable prop contract is satisfied.
  const [selected, setSelected] = useState<Set<string>>(new Set());

  function toggleRow(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAll(ids: string[], checked: boolean) {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const id of ids) {
        if (checked) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  }

  // ---- Saved filter views (ART-06) ---------------------------------------
  function handleSaveView() {
    const name = window.prompt('ตั้งชื่อมุมมองนี้')?.trim();
    if (!name) return;
    saveView({
      name,
      state: {
        tab,
        q,
        branchId: filterBranchId,
        departmentId: filterDepartmentId,
        termsFilter,
        dateFrom,
        dateTo,
      },
    });
    setSavedViews(loadViews());
  }

  function applyView(name: string) {
    const view = savedViews.find((v) => v.name === name);
    if (!view) return;
    const s = view.state;
    setTab(s.tab);
    setQ(s.q);
    setFilterBranchId(s.branchId);
    setFilterDepartmentId(s.departmentId);
    setTermsFilter(s.termsFilter);
    setDateFrom(s.dateFrom);
    setDateTo(s.dateTo);
  }

  const tabFilters = filtersForTab(tab);
  const {
    data: invoices,
    isPending,
    error,
  } = useArInvoices(companyId, {
    ...tabFilters,
    ...(drillCustomerId ? { customerId: drillCustomerId } : {}),
    ...(filterBranchId ? { branchId: filterBranchId } : {}),
    ...(filterDepartmentId ? { departmentId: filterDepartmentId } : {}),
    ...(dateFrom ? { dateFrom } : {}),
    ...(dateTo ? { dateTo } : {}),
  });

  // KPI aggregates: we want totals across ALL statuses (not just the active
  // tab). Make a second always-on query for the unfiltered list — cached
  // separately by query-key so toggling tabs doesn't re-fetch the totals row.
  // It also feeds ส่งออก so the exported file isn't scoped to the active tab.
  const { data: allInvoices } = useArInvoices(companyId, {});

  // ---- Collection forecast (ART-18) --------------------------------------
  // Reuses the cashflow-forecast pipeline (no backend change). We only request
  // it while the panel is expanded. The response buckets open AR collections
  // into the `AR_COL` category row; its `amounts[]` are one (signed) figure per
  // week in display order, aligned with `weeks[]`. We surface the absolute
  // inflow per week as a normalized prop for the bar widget.
  const { data: forecast } = useCashflowForecast(forecastOpen ? companyId : undefined, {
    weeks: 4,
  });
  const forecastWeeks = useMemo(() => {
    if (!forecast) return [];
    const arRow = forecast.rows.find((r) => r.category === 'AR_COL');
    return forecast.weeks.map((w, i) => ({
      label: `W${w.weekNumber}`,
      collections: Math.abs(toNumber(arRow?.amounts[i] ?? 0)),
    }));
  }, [forecast]);

  // Masters for code <-> id resolution on import/export.
  const { data: customers = [] } = useCustomers(companyId, true);
  const { data: branches = [] } = useBranches(companyId, true);
  const { data: departments = [] } = useDepartments(companyId, true);
  const createInvoice = useCreateArInvoice(companyId);

  const customerByCode = useMemo(
    () => new Map(customers.map((c) => [c.code.toLowerCase(), c.id])),
    [customers],
  );
  const customerCodeById = useMemo(
    () => new Map(customers.map((c) => [c.id, c.code])),
    [customers],
  );
  const customerNameById = useMemo(
    () => new Map(customers.map((c) => [c.id, c.nameTh])),
    [customers],
  );
  // Label for the customer-drill chip. The code is always resolvable; the name
  // is appended once the customer master has loaded.
  const drillCustomerLabel = useMemo(() => {
    if (!drillCustomerId) return '';
    const code = customerCodeById.get(drillCustomerId);
    const name = customerNameById.get(drillCustomerId);
    if (!code) return drillCustomerId;
    return name ? `${code} · ${name}` : code;
  }, [drillCustomerId, customerCodeById, customerNameById]);
  // customerId -> payment-terms code ('N30' / 'N60' / ...). Drives the
  // client-side เงื่อนไขชำระ filter; customers with no recorded terms are
  // simply absent from the map (and so never match a non-empty filter).
  const termsById = useMemo(
    () => new Map(customers.filter((c) => c.terms).map((c) => [c.id, c.terms!])),
    [customers],
  );
  const branchByCode = useMemo(
    () => new Map(branches.map((b) => [b.code.toLowerCase(), b.id])),
    [branches],
  );
  const branchCodeById = useMemo(() => new Map(branches.map((b) => [b.id, b.code])), [branches]);
  const departmentByCode = useMemo(
    () => new Map(departments.map((d) => [d.code.toLowerCase(), d.id])),
    [departments],
  );

  // ---- Export -------------------------------------------------------------
  const EXPORT_COLS: ExportColumn<ArInvoiceListRow>[] = useMemo(
    () => [
      { header: 'voucherNo', value: (r) => r.voucherNo, width: 18 },
      {
        header: 'customerCode',
        value: (r) => customerCodeById.get(r.customerId) ?? '',
        width: 14,
      },
      {
        header: 'branchCode',
        value: (r) => (r.branchId ? (branchCodeById.get(r.branchId) ?? '') : ''),
        width: 10,
      },
      { header: 'invoiceDate', value: (r) => String(r.invoiceDate).slice(0, 10), width: 12 },
      { header: 'dueDate', value: (r) => String(r.dueDate).slice(0, 10), width: 12 },
      { header: 'subtotal', value: (r) => Number(r.subtotal), width: 14 },
      { header: 'vatRate', value: (r) => Number(r.vatRate), width: 8 },
      { header: 'totalAmount', value: (r) => Number(r.totalAmount), width: 14 },
      { header: 'paidAmount', value: (r) => Number(r.paidAmount), width: 14 },
      // Read-only / computed columns — ignored on re-import.
      {
        header: 'outstanding',
        value: (r) => Number(r.totalAmount) - Number(r.paidAmount) - Number(r.creditedAmount),
        width: 14,
      },
      { header: 'agingDays', value: (r) => r.agingDays, width: 10 },
      { header: 'status', value: (r) => r.status, width: 10 },
    ],
    [customerCodeById, branchCodeById],
  );

  async function handleExport() {
    const filename = `ลูกหนี้การค้า_${company?.code ?? 'COMPANY'}_${todayStamp()}.xlsx`;
    await exportRows(filename, 'ลูกหนี้การค้า', EXPORT_COLS, allInvoices ?? []);
  }

  // ---- Import parse + run -------------------------------------------------
  function parse(grid: string[][]): {
    units: ParsedUnit<CreateArInvoice>[];
    headerError: string | null;
  } {
    const header = grid[0];
    if (!header) return { units: [], headerError: 'ไฟล์ว่างเปล่า' };
    const { idx, cell } = headerIndexer(header);
    for (const req of ['invoiceDate', 'dueDate', 'customerCode', 'amount']) {
      if (idx(req) === -1) return { units: [], headerError: `ไม่พบคอลัมน์ที่จำเป็น: ${req}` };
    }
    const units: ParsedUnit<CreateArInvoice>[] = [];
    for (let r = 1; r < grid.length; r++) {
      const cols = grid[r];
      if (!cols || cols.every((c) => c.trim() === '')) continue;
      const rowNo = r + 1;

      const customerCode = cell(cols, 'customerCode');
      const customerId = customerCode ? customerByCode.get(customerCode.toLowerCase()) : undefined;
      if (!customerId) {
        units.push({ rowNo, data: null, error: `ไม่พบลูกค้า: ${customerCode}` });
        continue;
      }
      const branchCode = cell(cols, 'branchCode');
      const branchId = branchCode ? branchByCode.get(branchCode.toLowerCase()) : undefined;
      if (branchCode && !branchId) {
        units.push({ rowNo, data: null, error: `ไม่พบสาขา: ${branchCode}` });
        continue;
      }
      const departmentCode = cell(cols, 'departmentCode');
      const departmentId = departmentCode
        ? departmentByCode.get(departmentCode.toLowerCase())
        : undefined;
      if (departmentCode && !departmentId) {
        units.push({ rowNo, data: null, error: `ไม่พบหน่วยงาน: ${departmentCode}` });
        continue;
      }

      const description = cell(cols, 'description');
      // Blank revenue code → the service resolves the configured SALES_DOMESTIC.
      const revenueCode = cell(cols, 'revenueCode');
      const amountStr = cell(cols, 'amount');
      const vatStr = cell(cols, 'vatRate');
      const currencyCode = cell(cols, 'currencyCode');

      // One row = one invoice carrying exactly one revenue line. The line
      // totals to `amount` via qty 1 + unitPrice amount.
      const line: CreateArInvoiceLine = {
        description: description || revenueCode,
        qty: 1,
        unitPrice: Number(amountStr),
        revenueCode,
      };
      const candidate = {
        invoiceDate: cell(cols, 'invoiceDate'),
        dueDate: cell(cols, 'dueDate'),
        customerId,
        ...(branchId ? { branchId } : {}),
        ...(departmentId ? { departmentId } : {}),
        ...(description ? { description } : {}),
        vatRate: vatStr ? Number(vatStr) : 7,
        ...(currencyCode ? { currencyCode } : {}),
        lines: [line],
      };
      const parsed = CreateArInvoiceSchema.safeParse(candidate);
      units.push(
        parsed.success
          ? { rowNo, data: parsed.data, error: null }
          : { rowNo, data: null, error: parsed.error.issues[0]?.message ?? 'ข้อมูลไม่ถูกต้อง' },
      );
    }
    return { units, headerError: null };
  }

  async function runImport(
    valid: ParsedUnit<CreateArInvoice>[],
    onProgress: (done: number, total: number) => void,
  ): Promise<ImportResult> {
    const res: ImportResult = { created: 0, updated: 0, failed: 0, errors: [] };
    for (let i = 0; i < valid.length; i++) {
      const u = valid[i];
      if (!u?.data) continue;
      try {
        await createInvoice.mutateAsync({
          data: u.data,
          idempotencyKey: crypto.randomUUID(),
        });
        res.created++;
      } catch (e) {
        res.failed++;
        res.errors.push({
          code: `แถว ${u.rowNo}`,
          message: e instanceof Error ? e.message : 'เกิดข้อผิดพลาด',
        });
      }
      onProgress(i + 1, valid.length);
    }
    return res;
  }

  // Snapshot "now" once per render via useState's lazy initialiser. The lint
  // rule react-hooks/purity flags Date.now() inside useMemo as an impure
  // calculation; staging it through state moves the impurity to render-time
  // initialisation which is allowed.
  const [nowMs] = useState<number>(() => Date.now());

  const filteredRows = useMemo(() => {
    if (!invoices) return [] as ArInvoiceListRow[];
    const needle = q.toLowerCase();
    return invoices.filter((r) => {
      if (
        needle &&
        !r.voucherNo.toLowerCase().includes(needle) &&
        !r.customerName.toLowerCase().includes(needle)
      ) {
        return false;
      }
      // The duesoon tab is a client refinement of the POSTED set served for it:
      // keep only rows due within the next 7 days (and not yet overdue).
      if (tab === 'duesoon' && !isDueSoon(r, nowMs)) return false;
      return matchesTerms(r.customerId, termsFilter, termsById);
    });
  }, [invoices, q, tab, nowMs, termsFilter, termsById]);

  // ---- Bulk selection ----------------------------------------------------
  // Resolve the checked id set against the currently filtered rows. A receipt
  // is customer-scoped, so bulk receipt is only meaningful when every selected
  // invoice belongs to one customer.
  const selectedRows = useMemo(
    () => filteredRows.filter((r) => selected.has(r.id)),
    [filteredRows, selected],
  );
  const singleCustomer = useMemo(
    () => new Set(selectedRows.map((r) => r.customerId)).size === 1,
    [selectedRows],
  );

  async function handleExportSelected() {
    const filename = `ลูกหนี้การค้า_${company?.code ?? 'COMPANY'}_${todayStamp()}.xlsx`;
    await exportRows(filename, 'ลูกหนี้การค้า', EXPORT_COLS, selectedRows);
  }

  function handleReceiveSelected() {
    const customerId = selectedRows[0]?.customerId;
    // Pre-tick every selected invoice in the receipt form (bulk preset). The
    // form drops any id not actually open for the customer, so this is safe.
    if (customerId) setReceiptPreset({ customerId, invoiceIds: selectedRows.map((r) => r.id) });
  }

  function clearSelection() {
    setSelected(new Set());
  }

  const totals = useMemo(() => {
    const list = allInvoices ?? [];
    let invoiced = 0;
    let received = 0;
    let outstanding = 0;
    let overdue = 0;
    // Net sales YTD = invoiced value excluding voided invoices. Feeds the DSO
    // (days sales outstanding) subtitle on the คงค้างชำระ KPI.
    let ytdNetSales = 0;
    for (const r of list) {
      const total = toNumber(r.totalAmount);
      const paid = toNumber(r.paidAmount);
      const credited = toNumber(r.creditedAmount);
      // outstanding = total − paid − credited (Phase 2b; credited reads 0 today).
      const open = total - paid - credited;
      invoiced += total;
      received += paid;
      outstanding += open;
      if (r.status !== 'VOID') ytdNetSales += total;
      const due = new Date(r.dueDate).getTime();
      if (r.status === 'POSTED' && due < nowMs) overdue += open;
    }
    // Days since Jan 1 of the current year (of `nowMs`). At least 1 so the
    // average-daily-sales denominator is never zero on the year's first day.
    const now = new Date(nowMs);
    const yearStart = new Date(now.getFullYear(), 0, 1).getTime();
    const daysElapsed = Math.max(1, Math.floor((nowMs - yearStart) / 86_400_000));
    const dso = computeDso(outstanding, ytdNetSales, daysElapsed);
    return { invoiced, received, outstanding, overdue, dso };
  }, [allInvoices, nowMs]);

  // Per-tab counts. Server-side count would need 4 round-trips; aggregating
  // from the full list is cheaper since we already have it cached.
  const tabCounts = useMemo(() => {
    const list = allInvoices ?? [];
    return {
      all: list.length,
      open: list.filter((r) => r.status === 'POSTED').length,
      overdue: list.filter((r) => r.status === 'POSTED' && new Date(r.dueDate).getTime() < nowMs)
        .length,
      duesoon: list.filter((r) => isDueSoon(r, nowMs)).length,
      paid: list.filter((r) => r.status === 'PAID').length,
    } satisfies Record<ArTab, number>;
  }, [allInvoices, nowMs]);

  return (
    <div className="page">
      <PageHead
        crumbs={['ลูกหนี้ / เจ้าหนี้', 'ลูกหนี้การค้า']}
        title="ลูกหนี้การค้า (Accounts Receivable)"
        actions={
          <>
            <HelpButton guide="ar" />
            <button
              className="btn"
              onClick={toggleDensity}
              aria-pressed={density === 'comfortable'}
              title={density === 'comfortable' ? 'สลับเป็นมุมมองกระชับ' : 'สลับเป็นมุมมองโปร่ง'}
            >
              <Icon name="eye" size={14} />{' '}
              {density === 'comfortable' ? 'มุมมองกระชับ' : 'มุมมองโปร่ง'}
            </button>
            <button className="btn" onClick={() => void navigate({ to: '/ar/aging' })}>
              <Icon name="reports" size={14} /> รายงานอายุหนี้
            </button>
            <button className="btn" onClick={() => void navigate({ to: '/ar/dso' })}>
              <Icon name="reports" size={14} /> DSO
            </button>
            <button className="btn" onClick={() => void navigate({ to: '/ar/receipts' })}>
              <Icon name="ar" size={14} /> ใบรับชำระ
            </button>
            <button className="btn" onClick={() => void navigate({ to: '/ar/credit-notes' })}>
              <Icon name="reports" size={14} /> ใบลดหนี้
            </button>
            <button className="btn" onClick={() => void navigate({ to: '/ar/voids' })}>
              <Icon name="reports" size={14} /> ใบยกเลิก
            </button>
            <button className="btn" onClick={() => setShowImport(true)} disabled={!companyId}>
              <Icon name="upload" size={14} /> นำเข้า
            </button>
            <button
              className="btn"
              onClick={() => void handleExport()}
              disabled={(allInvoices?.length ?? 0) === 0}
            >
              <Icon name="download" size={14} /> ส่งออก
            </button>
            <button className="btn" onClick={() => void navigate({ to: '/ar/billing-notes' })}>
              <Icon name="print" size={14} /> ใบวางบิล
            </button>
            <button className="btn" onClick={() => void navigate({ to: '/ar/pdc' })}>
              <Icon name="ar" size={14} /> เช็ครับ (PDC)
            </button>
            <button className="btn" onClick={() => void navigate({ to: '/ar/advances' })}>
              <Icon name="ar" size={14} /> รับล่วงหน้า
            </button>
            <button className="btn" onClick={() => setReceiptPreset({})} disabled={!companyId}>
              <Icon name="ar" size={14} /> รับชำระ
            </button>
            <button className="btn primary" onClick={() => setCreateOpen(true)}>
              <Icon name="plus" size={14} /> สร้างใบแจ้งหนี้
            </button>
          </>
        }
      />

      <div className="kpi-grid">
        <div className="kpi">
          <div className="label">
            <span className="dot" />
            ใบแจ้งหนี้รวม
          </div>
          <div className="value tnum">
            {fmtB(totals.invoiced)} <span className="unit">บาท</span>
          </div>
          <div className="trend faint">{allInvoices?.length ?? 0} ใบ · YTD</div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--positive)' }} />
            รับชำระแล้ว
          </div>
          <div className="value tnum">
            {fmtB(totals.received)} <span className="unit">บาท</span>
          </div>
          <div className="trend pos">
            <Icon name="check" size={11} />{' '}
            {totals.invoiced > 0 ? Math.round((totals.received / totals.invoiced) * 100) : 0}%
            ของยอดรวม
          </div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--warning)' }} />
            คงค้างชำระ
          </div>
          <div className="value tnum">
            {fmtB(totals.outstanding)} <span className="unit">บาท</span>
          </div>
          <div className="trend faint">
            {totals.dso === null ? 'รวมยอดที่ยังไม่ได้รับชำระ' : `DSO เฉลี่ย ${totals.dso} วัน`}
          </div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--negative)' }} />
            เกินกำหนด
          </div>
          <div className="value tnum">
            {fmtB(totals.overdue)} <span className="unit">บาท</span>
          </div>
          <div className="trend down">{tabCounts.overdue} ใบ · ต้องติดตาม</div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 'var(--section-gap)' }}>
        <div
          className="card-head"
          style={{ cursor: 'pointer' }}
          onClick={() => setForecastOpen((v) => !v)}
        >
          <div>
            <h3>คาดการณ์การรับชำระ (4 สัปดาห์)</h3>
            <div className="sub">ประมาณการรับชำระจากลูกหนี้คงค้าง · หน่วย: บาท</div>
          </div>
          <button
            type="button"
            className="btn ghost"
            onClick={(e) => {
              e.stopPropagation();
              setForecastOpen((v) => !v);
            }}
            aria-expanded={forecastOpen}
          >
            <Icon name={forecastOpen ? 'chevron' : 'chevronD'} size={14} />
            {forecastOpen ? 'ซ่อน' : 'แสดง'}
          </button>
        </div>
        {forecastOpen && (
          <div className="card-body">
            {forecastWeeks.length > 0 ? (
              <CollectionForecast weeks={forecastWeeks} />
            ) : (
              <div className="muted" style={{ padding: 20, textAlign: 'center' }}>
                กำลังโหลดประมาณการ…
              </div>
            )}
          </div>
        )}
      </div>

      <div className="page-tabs">
        {AR_TABS.map(([k, l]) => (
          <div key={k} className={`pt ${tab === k ? 'active' : ''}`} onClick={() => setTab(k)}>
            {l}{' '}
            <span className="faint" style={{ marginLeft: 4 }}>
              ({tabCounts[k]})
            </span>
          </div>
        ))}
      </div>

      <div className="tbl-shell">
        <div className="filter-row">
          <input
            className="input search w-lg"
            placeholder="ค้นหา เลขที่ใบแจ้งหนี้ / ชื่อลูกค้า"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            style={{
              paddingLeft: 28,
              backgroundImage: SEARCH_BG,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: '8px center',
            }}
          />
          <DimensionFilter
            companyId={companyId}
            branchId={filterBranchId}
            departmentId={filterDepartmentId}
            onBranchChange={setFilterBranchId}
            onDepartmentChange={setFilterDepartmentId}
          />
          <select
            className="select"
            value={termsFilter}
            onChange={(e) => setTermsFilter(e.target.value)}
            aria-label="เงื่อนไขชำระ"
          >
            <option value="">เงื่อนไขชำระ: ทั้งหมด</option>
            <option value="N30">Net 30</option>
            <option value="N60">Net 60</option>
          </select>
          <input
            className="input w-sm"
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            aria-label="วันที่เริ่มต้น"
          />
          <span className="faint">ถึง</span>
          <input
            className="input w-sm"
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            aria-label="วันที่สิ้นสุด"
          />
          <button className="btn" onClick={handleSaveView}>
            <Icon name="check" size={14} /> บันทึกมุมมอง
          </button>
          {savedViews.length > 0 && (
            <select
              className="select"
              value=""
              onChange={(e) => {
                if (e.target.value) applyView(e.target.value);
              }}
              aria-label="มุมมองที่บันทึก"
            >
              <option value="">มุมมองที่บันทึก</option>
              {savedViews.map((v) => (
                <option key={v.name} value={v.name}>
                  {v.name}
                </option>
              ))}
            </select>
          )}
        </div>
        {drillCustomerId && (
          <div style={{ padding: '0 0 10px' }}>
            <span className="chip active">
              ลูกค้า: {drillCustomerLabel}
              <button
                type="button"
                className="btn ghost"
                aria-label="ล้างตัวกรองลูกค้า"
                onClick={() => void navigate({ to: '/ar', search: {} })}
                style={{ padding: '0 4px', height: 20, lineHeight: 1 }}
              >
                <Icon name="x" size={12} />
              </button>
            </span>
          </div>
        )}
        <BulkBar
          count={selectedRows.length}
          singleCustomer={singleCustomer}
          onExport={() => void handleExportSelected()}
          onReceive={handleReceiveSelected}
          onClear={clearSelection}
        />
        {isPending || error ? (
          <div className="tbl-wrap" style={{ maxHeight: 540 }}>
            <table className="tbl">
              <tbody>
                {isPending && (
                  <tr>
                    <td colSpan={11} className="muted" style={{ textAlign: 'center', padding: 20 }}>
                      กำลังโหลด…
                    </td>
                  </tr>
                )}
                {error && (
                  <tr>
                    <td colSpan={11} className="neg" style={{ textAlign: 'center', padding: 20 }}>
                      โหลดไม่สำเร็จ: {error.message}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <ArInvoiceTable
            rows={filteredRows}
            selected={selected}
            onToggle={toggleRow}
            onToggleAll={toggleAll}
            onOpen={(id) => {
              void navigate({ to: '/ar/invoices/$id', params: { id } });
            }}
            onReceive={(row) => setReceiptPreset({ customerId: row.customerId, invoiceId: row.id })}
            onVoid={(row) => {
              // Voiding runs from the detail page (it hosts the VoidInvoiceModal +
              // approval-state banner) — same navigation idiom as เปิดดู.
              void navigate({ to: '/ar/invoices/$id', params: { id: row.id } });
            }}
            nowMs={nowMs}
          />
        )}
      </div>

      {showImport && companyId && (
        <SpreadsheetImportModal<CreateArInvoice>
          title="นำเข้าใบแจ้งหนี้ลูกหนี้"
          intro={
            <>
              หนึ่งแถว = หนึ่งใบแจ้งหนี้ ที่มี<b>รายการรายได้รายการเดียว</b> —
              ใบแจ้งหนี้แบบหลายรายการ ให้ใช้ปุ่ม “สร้างใบแจ้งหนี้”
            </>
          }
          fields={AR_IMPORT_FIELDS}
          templateColumns={AR_TEMPLATE_COLUMNS}
          templateExamples={AR_TEMPLATE_EXAMPLES}
          templateFilename="ar_invoice_template.xlsx"
          sheetName="ลูกหนี้การค้า"
          parse={parse}
          previewColumns={[
            {
              header: 'ลูกค้า',
              render: (d) => customerCodeById.get(d.customerId) ?? d.customerId,
            },
            { header: 'วันที่ออก', render: (d) => d.invoiceDate },
            { header: 'กำหนดชำระ', render: (d) => d.dueDate },
            {
              header: 'ยอดเงิน',
              width: 120,
              render: (d) =>
                fmt(
                  d.lines.reduce((s, l) => s + Number(l.qty) * Number(l.unitPrice), 0),
                  2,
                ),
            },
          ]}
          runImport={runImport}
          onClose={() => setShowImport(false)}
        />
      )}
      {createOpen && companyId && (
        <CreateInvoiceModal companyId={companyId} onClose={() => setCreateOpen(false)} />
      )}
      {receiptPreset && companyId && (
        <RecordReceiptModal
          companyId={companyId}
          presetCustomerId={receiptPreset.customerId}
          presetInvoiceId={receiptPreset.invoiceId}
          presetInvoiceIds={receiptPreset.invoiceIds}
          onClose={() => setReceiptPreset(null)}
        />
      )}
    </div>
  );
}

// =============================================================================
// Modal: Create AR Invoice
// =============================================================================

interface CreateInvoiceModalProps {
  companyId: string;
  onClose: () => void;
}

function CreateInvoiceModal({ companyId, onClose }: CreateInvoiceModalProps) {
  const { data: customers } = useCustomers(companyId, true);
  const { data: coa } = useCoa(companyId, true);
  const create = useCreateArInvoice(companyId);
  const { can } = usePermissions();

  const today = new Date();
  const defaultDue = new Date(today);
  defaultDue.setDate(defaultDue.getDate() + 30);

  const [customerId, setCustomerId] = useState('');
  const [branchId, setBranchId] = useState<string>('');
  const [departmentId, setDepartmentId] = useState<string>('');
  const [invoiceDate, setInvoiceDate] = useState(dateToInputValue(today));
  const [dueDate, setDueDate] = useState(dateToInputValue(defaultDue));
  // Once the user edits the due date manually, stop auto-deriving it from terms.
  const [dueDateTouched, setDueDateTouched] = useState(false);
  const [vatRate, setVatRate] = useState<number>(7);
  const [description, setDescription] = useState('');
  const [currencyCode, setCurrencyCode] = useState<string>('THB');
  const [foreignAmount, setForeignAmount] = useState<number>(0);
  const [exchangeRate, setExchangeRate] = useState<number>(0);
  const [lines, setLines] = useState<CreateArInvoiceLine[]>([
    { description: '', qty: 1, unitPrice: 0, revenueCode: '' },
  ]);
  // Maker's tick to override a credit-limit hold (only sent when over-limit AND
  // the user holds ar.invoice.creditoverride — the server is the authority).
  const [overrideChecked, setOverrideChecked] = useState(false);

  // Auto-derive the due date from the selected customer's payment terms
  // (e.g. 'N30' → invoiceDate + 30 days) until the user edits it by hand.
  // Derived inside the customer / invoice-date change handlers rather than a
  // useEffect (the repo lints against set-state-in-effect).
  function deriveDueDate(custId: string, invDate: string) {
    if (dueDateTouched) return;
    const customer = customers?.find((c) => c.id === custId);
    const n = parseTermsDays(customer?.terms);
    if (n == null || !invDate) return;
    const base = new Date(`${invDate}T00:00:00`);
    if (Number.isNaN(base.getTime())) return;
    setDueDate(dateToInputValue(addDays(base, n)));
  }

  const isForeign = currencyCode !== 'THB';
  // Derived THB total for the foreign-currency preview (foreignAmount × rate).
  // Guard against NaN/empty FX inputs mid-entry (Number('') → NaN) so the
  // preview + credit bar don't render NaN.
  const derivedThbTotalRaw = foreignAmount * exchangeRate;
  const derivedThbTotal = Number.isFinite(derivedThbTotalRaw) ? derivedThbTotalRaw : 0;
  // For a foreign-currency invoice the API requires both the foreign amount and
  // an exchange rate; gate the create button until both are positive.
  const fxInvalid = isFxIncomplete(currencyCode, foreignAmount, exchangeRate);

  // REVENUE accounts only. Postable leaf accounts are non-control (every real
  // account is a leaf with isControl=false); control/header accounts can't be
  // posted to, so we exclude them from the dropdown.
  const revenueAccounts = useMemo(() => {
    if (!coa) return [];
    return coa.filter((c) => c.type === 'REVENUE' && c.isActive && !c.isControl);
  }, [coa]);

  // Cash/bank accounts for the receipt modal use the same heuristic but for
  // ASSET codes in the 11xx range. Exposed via revenueAccounts.length check
  // below — kept here so both modals share one COA fetch.

  const subtotal = useMemo(
    () => lines.reduce((s, l) => s + Number(l.qty || 0) * Number(l.unitPrice || 0), 0),
    [lines],
  );
  const vatAmount = useMemo(() => (subtotal * vatRate) / 100, [subtotal, vatRate]);
  const totalAmount = subtotal + vatAmount;

  // The THB amount the SERVER will book as the invoice total — and therefore the
  // figure the credit limit is checked against. For a FOREIGN-currency invoice
  // the server derives totalAmount = foreignAmount × exchangeRate (VAT-inclusive,
  // see ar-invoices.service.ts), NOT the line-derived subtotal+VAT. Use this
  // FX-aware figure for the credit hold + bar so the client preview matches the
  // server (a THB invoice keeps creditTotal === totalAmount).
  const creditTotal = isForeign ? derivedThbTotal : totalAmount;

  // Credit-limit awareness (AR-08). Fetch the selected customer's exposure vs
  // limit; render a bar and — when this draft pushes projected exposure over the
  // limit — hold submission unless a permitted user overrides. creditLimit 0 (or
  // no status yet) ⇒ unlimited: no bar, no hold.
  const { data: creditStatus } = useArCreditStatus(companyId, customerId || undefined);
  const creditLimitNum = creditStatus ? Number(creditStatus.creditLimit) : 0;
  const projectedOver =
    !!creditStatus &&
    creditLimitNum > 0 &&
    Number(creditStatus.exposure) + creditTotal > creditLimitNum;
  const canOverride = can('ar.invoice.creditoverride');
  // Submit is blocked when over-limit AND the user can't (or hasn't) overridden.
  const creditBlocked = projectedOver && !(canOverride && overrideChecked);
  // The flag actually sent — only meaningful when over-limit and ticked.
  const sendCreditOverride = projectedOver && canOverride && overrideChecked;

  function updateLine(idx: number, patch: Partial<CreateArInvoiceLine>) {
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  }

  function addLine() {
    setLines((prev) => [...prev, { description: '', qty: 1, unitPrice: 0, revenueCode: '' }]);
  }

  function removeLine(idx: number) {
    setLines((prev) => prev.filter((_, i) => i !== idx));
  }

  async function handleSubmit() {
    const payload: CreateArInvoice = {
      invoiceDate,
      dueDate,
      customerId,
      ...(branchId ? { branchId } : {}),
      ...(departmentId ? { departmentId } : {}),
      ...(description ? { description } : {}),
      vatRate,
      // Only request the override when over-limit AND a permitted user ticked the
      // box. The server re-checks the limit and the permission — this is intent.
      creditOverride: sendCreditOverride,
      ...buildFxFields(currencyCode, foreignAmount, exchangeRate),
      lines: lines.filter((l) => l.description && Number(l.qty) > 0 && Number(l.unitPrice) >= 0),
    };
    await create.mutateAsync({ data: payload });
    onClose();
  }

  const disabled =
    !customerId ||
    !invoiceDate ||
    !dueDate ||
    lines.length === 0 ||
    subtotal <= 0 ||
    fxInvalid ||
    creditBlocked ||
    create.isPending;

  return (
    <div style={modalOverlay} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={modalBox}>
        <div style={modalHeader}>
          <h3 style={modalTitle}>สร้างใบแจ้งหนี้</h3>
          <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
            <Icon name="x" size={18} />
          </button>
        </div>
        <div style={modalBody}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <div>
              <label style={modalLabel}>ลูกค้า</label>
              <select
                className="select"
                value={customerId}
                onChange={(e) => {
                  setCustomerId(e.target.value);
                  deriveDueDate(e.target.value, invoiceDate);
                }}
                style={{ width: '100%', height: 38, fontSize: 14 }}
              >
                <option value="">— เลือกลูกค้า —</option>
                {customers?.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.code} · {c.nameTh}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label style={modalLabel}>สาขา</label>
              <BranchSelect
                companyId={companyId}
                value={branchId}
                onChange={setBranchId}
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
            </div>
            <div>
              <label style={modalLabel}>หน่วยงาน</label>
              <DepartmentSelect
                companyId={companyId}
                value={departmentId}
                onChange={setDepartmentId}
                branchId={branchId}
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 120px', gap: 12 }}>
            <div>
              <label style={modalLabel}>วันที่ออก</label>
              <input
                type="date"
                className="input"
                value={invoiceDate}
                onChange={(e) => {
                  setInvoiceDate(e.target.value);
                  deriveDueDate(customerId, e.target.value);
                }}
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
            </div>
            <div>
              <label style={modalLabel}>กำหนดชำระ (Net 30)</label>
              <input
                type="date"
                className="input"
                value={dueDate}
                onChange={(e) => {
                  setDueDate(e.target.value);
                  setDueDateTouched(true);
                }}
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
            </div>
            <div>
              <label style={modalLabel}>VAT (%)</label>
              <input
                type="number"
                className="input tnum"
                value={vatRate}
                onChange={(e) => setVatRate(Number(e.target.value))}
                min={0}
                max={100}
                step={0.01}
                style={{ width: '100%', height: 38, fontSize: 14, textAlign: 'right' }}
              />
            </div>
          </div>
          <div>
            <label style={modalLabel}>คำอธิบาย</label>
            <input
              className="input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="เช่น ขายสินค้าเซรามิค ใบสั่งซื้อ #..."
              style={{ width: '100%', height: 38, fontSize: 14 }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr 1fr', gap: 12 }}>
            <div>
              <label style={modalLabel}>สกุลเงิน</label>
              <select
                className="select"
                value={currencyCode}
                onChange={(e) => setCurrencyCode(e.target.value)}
                style={{ width: '100%', height: 38, fontSize: 14 }}
              >
                <option value="THB">THB · บาท</option>
                <option value="USD">USD · ดอลลาร์สหรัฐ</option>
                <option value="EUR">EUR · ยูโร</option>
                <option value="CNY">CNY · หยวน</option>
                <option value="JPY">JPY · เยน</option>
              </select>
            </div>
            {isForeign && (
              <>
                <div>
                  <label style={modalLabel}>ยอดเงินสกุลต่างประเทศ ({currencyCode})</label>
                  <input
                    type="number"
                    className="input tnum"
                    value={foreignAmount}
                    min={0}
                    step={0.01}
                    onChange={(e) => setForeignAmount(Number(e.target.value))}
                    style={{ width: '100%', height: 38, fontSize: 14, textAlign: 'right' }}
                  />
                </div>
                <div>
                  <label style={modalLabel}>อัตราแลกเปลี่ยน (THB/{currencyCode})</label>
                  <input
                    type="number"
                    className="input tnum"
                    value={exchangeRate}
                    min={0}
                    step={0.0001}
                    onChange={(e) => setExchangeRate(Number(e.target.value))}
                    style={{ width: '100%', height: 38, fontSize: 14, textAlign: 'right' }}
                  />
                </div>
              </>
            )}
          </div>
          {isForeign && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr auto',
                fontSize: 14,
              }}
            >
              <div className="muted">มูลค่าโดยประมาณ (THB)</div>
              <div className="tnum" style={{ textAlign: 'right' }}>
                {fmt(derivedThbTotal, 2)}
              </div>
            </div>
          )}

          <div>
            <label style={modalLabel}>รายการ</label>
            <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
              <table className="tbl">
                <thead>
                  <tr>
                    <th>คำอธิบาย</th>
                    <th className="num" style={{ width: 80 }}>
                      จำนวน
                    </th>
                    <th className="num" style={{ width: 110 }}>
                      ราคา/หน่วย
                    </th>
                    <th style={{ width: 160 }}>บัญชีรายได้</th>
                    <th className="num" style={{ width: 110 }}>
                      ยอดเงิน
                    </th>
                    <th style={{ width: 30 }} />
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l, idx) => {
                    const amount = Number(l.qty || 0) * Number(l.unitPrice || 0);
                    return (
                      <tr key={idx}>
                        <td>
                          <input
                            className="input"
                            value={l.description}
                            onChange={(e) => updateLine(idx, { description: e.target.value })}
                            style={{ width: '100%', height: 32, fontSize: 13 }}
                          />
                        </td>
                        <td className="num">
                          <input
                            type="number"
                            className="input tnum"
                            value={l.qty}
                            min={0}
                            step={0.0001}
                            onChange={(e) => updateLine(idx, { qty: Number(e.target.value) })}
                            style={{
                              width: '100%',
                              height: 32,
                              fontSize: 13,
                              textAlign: 'right',
                            }}
                          />
                        </td>
                        <td className="num">
                          <input
                            type="number"
                            className="input tnum"
                            value={l.unitPrice}
                            min={0}
                            step={0.01}
                            onChange={(e) => updateLine(idx, { unitPrice: Number(e.target.value) })}
                            style={{
                              width: '100%',
                              height: 32,
                              fontSize: 13,
                              textAlign: 'right',
                            }}
                          />
                        </td>
                        <td>
                          <select
                            className="select"
                            value={l.revenueCode ?? ''}
                            onChange={(e) => updateLine(idx, { revenueCode: e.target.value })}
                            style={{ width: '100%', height: 32, fontSize: 13 }}
                          >
                            <option value="">— ค่าเริ่มต้น (รายได้ขายในประเทศ ตามตั้งค่า) —</option>
                            {revenueAccounts.length === 0 && l.revenueCode && (
                              <option value={l.revenueCode}>{l.revenueCode}</option>
                            )}
                            {revenueAccounts.map((a) => (
                              <option key={a.code} value={a.code}>
                                {a.code} · {a.nameTh}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="num tnum">{fmt(amount, 2)}</td>
                        <td>
                          {lines.length > 1 && (
                            <button
                              className="btn ghost"
                              onClick={() => removeLine(idx)}
                              style={{ padding: '0 6px' }}
                            >
                              <Icon name="x" size={14} />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <button className="btn ghost" onClick={addLine} style={{ marginTop: 8, fontSize: 13 }}>
              <Icon name="plus" size={12} /> เพิ่มรายการ
            </button>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr auto',
              rowGap: 4,
              fontSize: 14,
              marginTop: 8,
            }}
          >
            <div className="muted">ยอดก่อน VAT</div>
            <div className="tnum" style={{ textAlign: 'right' }}>
              {fmt(subtotal, 2)}
            </div>
            <div className="muted">VAT ({vatRate}%)</div>
            <div className="tnum" style={{ textAlign: 'right' }}>
              {fmt(vatAmount, 2)}
            </div>
            <div style={{ fontWeight: 600 }}>รวมทั้งสิ้น</div>
            <div className="tnum" style={{ textAlign: 'right', fontWeight: 600 }}>
              {fmt(totalAmount, 2)}
            </div>
          </div>

          {/* Credit-limit awareness (AR-08). The bar self-hides when the customer
              has no limit (creditLimit 0). When over-limit, a permitted user sees
              the override checkbox; everyone else is blocked at submit. */}
          {creditStatus && (
            <CreditLimitBar
              status={creditStatus}
              draftTotal={creditTotal}
              projectedOver={projectedOver}
            />
          )}
          {projectedOver && (
            <Can permission="ar.invoice.creditoverride">
              <label
                style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}
                data-testid="ar-credit-override-label"
              >
                <input
                  type="checkbox"
                  checked={overrideChecked}
                  onChange={(e) => setOverrideChecked(e.target.checked)}
                  data-testid="ar-credit-override-checkbox"
                />
                ยืนยัน override วงเงินเครดิต
              </label>
            </Can>
          )}
          {creditBlocked && (
            <div
              style={{ color: 'var(--negative)', fontSize: 13 }}
              data-testid="ar-credit-blocked-hint"
            >
              {canOverride
                ? 'เกินวงเงิน — ติ๊ก override เพื่อดำเนินการต่อ'
                : 'เกินวงเงิน — ต้องให้ผู้มีสิทธิ์อนุมัติ'}
            </div>
          )}

          {create.error && <div style={modalError}>บันทึกไม่สำเร็จ: {create.error.message}</div>}
        </div>
        <div style={modalFooter}>
          <button className="btn ghost" onClick={onClose}>
            ยกเลิก
          </button>
          <button
            className="btn primary"
            disabled={disabled}
            onClick={() => {
              void handleSubmit();
            }}
          >
            <Icon name="check" size={14} /> {create.isPending ? 'กำลังบันทึก…' : 'สร้างใบแจ้งหนี้'}
          </button>
        </div>
      </div>
    </div>
  );
}
